<script setup>
import { reactive, onMounted, ref, computed, watch } from 'vue'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import ModalFormularioV2 from '@/components/base/modal/ModalFormularioV2.vue'
import ConfirmationModal from '@/components/base/modal/ConfirmationModal.vue'
import { criarConfigBuscaParceiroInstituicaoFinanceira } from '@/search-definitions/parceiroInstituicaoFinanceiraSearch'
import { criarConfigBuscaTituloOriginal } from '@/search-definitions/tituloFinanceiroBuscaSearch'
import { criarConfigBuscaDocumentoEntrada } from '@/search-definitions/documentoEntradaSearch'
import { criarConfigBuscaCondicaoPagamento } from '@/search-definitions/condicaoPagamentoSearch'
import { criarConfigBuscaLimiteCredito } from '@/search-definitions/limiteCreditoSearch'
import { criarConfigBuscaPedidoVenda } from '@/search-definitions/pedidoVendaSearch'
import { dataHojeBR } from '@/utils/useDataBR'
import { FIN_FINANCIAMENTO_ST } from '@/constants/FIN_FINANCIAMENTO_ST'
import { FIN_FINANCIAMENTO_ORIGEM } from '@/constants/FIN_FINANCIAMENTO_ORIGEM'
import api from '@/services/api'

const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const props = defineProps({
  itemParaEditar: { type: Object, default: null },
  modeloVazio: {
    type: Object,
    default: () => ({
      idOperacaoFinanciamento: 0,
      idInstituicaoFinanceira: null,
      nomeInstituicaoFinanceira: '',
      idLimiteCredito: null,
      descricaoLimiteCredito: '',
      lstTipoFinanciamento: '',
      lstStatusFinanciamento: '',
      idCondicaoPagamento: null,
      dataFechamento: dataHojeBR(),
      dataDesembolso: dataHojeBR(),
      taxaJurosAnual: null,
      valorFinanciado: null,
      valorJuros: null,
      valorIof: 0,
      lstOrigemFinanciamento: '',
      lstMoeda: '',
      observacaoLivre: '',
      titulosOriginais: [], // lista de { idTituloFinanceiro, valorAlocado, numeroDocumento, nomeFornecedor, saldoDevedor }
      pedidosVenda: [],
      idPedidoVendaSelecao: null
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  handleCancel
} = useFormulario(props, emit)

const listas = reactive({})
const isLoadingListas = ref(false)
const mostrarAuditoria = ref(false)
const abrirAuditoria = () => { mostrarAuditoria.value = true }

const accordionState = reactive({
  estruturacao: true,
  valores: false,
  status: false,
  dividas: false
})

const temPermissaoCompleta = computed(() =>
  authStore.isAdminInterno || authStore.isAdminCliente || (authStore.permissoes && authStore.permissoes.includes('MODULO_COMPLETO'))
)

const isReadOnly = computed(() => {
  if (!isEditing.value) return false
  if (!temPermissaoCompleta.value) return true
  const status = item.value.lstStatusFinanciamento
  if (status && status !== FIN_FINANCIAMENTO_ST.EmDigitacao) return true
  return false
})

// --- Lógica "Salvar e Continuar" (padrão CategoriasFinanceirasCadastro) ---
const isFinanciamentoSaved = ref(isEditing.value)
const isSavingFinanciamentoLocal = ref(false)
const isTitulosFrameBlocked = computed(() => !isFinanciamentoSaved.value || isReadOnly.value)

// --- AÇÕES DE TRANSIÇÃO DE STATUS (Toolbar) ---
const isLoadingAcao = ref(false)
const confirmModalRef = ref(null)
const acaoPendente = ref(null)
const mensagemConfirmacao = ref('Tem certeza que deseja realizar esta ação?')

const statusAtual = computed(() => item.value.lstStatusFinanciamento)

const podeContratar = computed(() =>
  isFinanciamentoSaved.value &&
  statusAtual.value === FIN_FINANCIAMENTO_ST.EmDigitacao &&
  temPermissaoCompleta.value
)

const podeCancelar = computed(() =>
  isFinanciamentoSaved.value &&
  statusAtual.value === FIN_FINANCIAMENTO_ST.EmDigitacao &&
  temPermissaoCompleta.value
)

const executarAcao = async (endpoint, mensagemSucesso, mensagemErro) => {
  if (isLoadingAcao.value) return
  isLoadingAcao.value = true
  try {
    await api.post(`/OperacoesFinanciamentos/${item.value.idOperacaoFinanciamento}${endpoint}`, { observacao: '' })
    const response = await api.get(`/OperacoesFinanciamentos/${item.value.idOperacaoFinanciamento}`)
    item.value = response.data
    emit('show-notification', { message: mensagemSucesso, type: 'success' })
  } catch (error) {
    const msg = error.response?.data?.message || mensagemErro
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoadingAcao.value = false
  }
}

const confirmarContratar = () => {
  const origem = item.value.lstOrigemFinanciamento
  if (origem === FIN_FINANCIAMENTO_ORIGEM.Divida) {
    // Grupo A: validar alocação antes de contratar
    const erroAlocacao = validarAlocacao()
    if (erroAlocacao) {
      emit('show-notification', { message: erroAlocacao, type: 'error' })
      return
    }
    acaoPendente.value = 'contratar'
    mensagemConfirmacao.value = 'Tem certeza que deseja contratar este financiamento de dívida? Os títulos originais serão baixados, novos títulos gerados e o limite consumido.'
  } else if (origem === FIN_FINANCIAMENTO_ORIGEM.Recebiveis) {
    // Recebíveis: valida que há títulos A RECEBER vinculados
    if (!item.value.titulosOriginais || item.value.titulosOriginais.length === 0) {
      emit('show-notification', { message: 'Vincule ao menos um título A RECEBER antes de contratar.', type: 'error' })
      return
    }
    acaoPendente.value = 'contratar'
    mensagemConfirmacao.value = 'Tem certeza que deseja contratar este financiamento de recebíveis? O banco comprará os recebíveis com deságio. NÃO serão geradas parcelas ao banco.'
  } else {
    // Direto: sem validação de alocação
    acaoPendente.value = 'contratar'
    mensagemConfirmacao.value = 'Tem certeza que deseja contratar este financiamento? Após contratado, os títulos serão gerados e o limite consumido.'
  }
  confirmModalRef.value.show()
}

const confirmarCancelar = () => {
  acaoPendente.value = 'cancelar'
  mensagemConfirmacao.value = 'Tem certeza que deseja cancelar este financiamento? Esta ação não poderá ser desfeita.'
  confirmModalRef.value.show()
}

const onConfirmAcao = async (confirmado) => {
  if (!confirmado || !acaoPendente.value) {
    acaoPendente.value = null
    return
  }
  if (acaoPendente.value === 'contratar') {
    await executarAcao('/contratar', 'Financiamento contratado com sucesso!', 'Erro ao contratar o financiamento.')
  } else if (acaoPendente.value === 'cancelar') {
    await executarAcao('/cancelar', 'Financiamento cancelado com sucesso.', 'Erro ao cancelar o financiamento.')
  }
  acaoPendente.value = null
}

// --- CONFIGURAÇÕES DOS CAMPOS DE BUSCA ---
const configBuscaBanco = computed(() => criarConfigBuscaParceiroInstituicaoFinanceira(authStore.organizacaoAtiva))
const configBuscaTitulo = computed(() => criarConfigBuscaTituloOriginal(authStore.organizacaoAtiva))
const configBuscaDocumentoEntrada = computed(() => criarConfigBuscaDocumentoEntrada(authStore.organizacaoAtiva))
const configBuscaCondicaoPagamento = computed(() => criarConfigBuscaCondicaoPagamento())
const configBuscaLimiteCredito = computed(() => criarConfigBuscaLimiteCredito(authStore.organizacaoAtiva, item.value.idInstituicaoFinanceira))
const configBuscaPedidoVenda = computed(() => criarConfigBuscaPedidoVenda(authStore.organizacaoAtiva))

const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    const result = await metadataStore.carregarListasParaTabelas(['OperacaoFinanciamento'])
    Object.assign(listas, result)
  } catch (e) {
    console.warn('Erro ao carregar listas:', e)
  } finally {
    isLoadingListas.value = false
  }
}

const getTextoLista = (codigo, nomeLista) => {
  if (!codigo) return '-'
  const lista = listas[nomeLista]
  if (!lista) return codigo
  const itemLista = lista.find(i => String(i.idListaClienteItem) === String(codigo) || String(i.idListaSistemaItem) === String(codigo))
  return itemLista ? itemLista.textoLista : codigo
}

const customEntityInfo = computed(() => {
  if (!isEditing.value) return entityInfo.value
  const nome = item.value.nomeInstituicaoFinanceira || `Financiamento #${item.value.idOperacaoFinanciamento}`
  return {
    id: entityInfo.value.id,
    name: nome,
    nameLabel: 'Financiamento'
  }
})

const statusExibicao = computed(() => getTextoLista(item.value.lstStatusFinanciamento, 'LstStatusFinanciamento'))

// --- TÍTULOS ORIGINAIS (Grupo A: Forfait / Risco Sacado) ---
const documentoSelecionado = ref(null)
const isLoadingTitulosDocumento = ref(false)

const totalValorAlocado = computed(() => {
  return (item.value.titulosOriginais || []).reduce((acc, t) => acc + (Number(t.valorAlocado) || 0), 0)
})

// Validação: todo valor tomado no financiamento deve estar alocado nos títulos
const valorAlocadoExcede = computed(() => {
  if (item.value.lstOrigemFinanciamento !== FIN_FINANCIAMENTO_ORIGEM.Divida) return false
  const financiado = Number(item.value.valorFinanciado) || 0
  return financiado > 0 && totalValorAlocado.value > financiado
})

const valorAlocadoIncompleto = computed(() => {
  if (item.value.lstOrigemFinanciamento !== FIN_FINANCIAMENTO_ORIGEM.Divida) return false
  const financiado = Number(item.value.valorFinanciado) || 0
  return financiado > 0 && totalValorAlocado.value < financiado
})

const formatarValor = (valor) => {
  if (valor == null || isNaN(valor)) return '-'
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor)
}

const recarregarItem = async () => {
  if (!item.value.idOperacaoFinanciamento) return
  try {
    const resp = await api.get(`/OperacoesFinanciamentos/${item.value.idOperacaoFinanciamento}`)
    item.value = resp.data
  } catch (e) {
    console.error('Erro ao recarregar financiamento:', e)
  }
}

const adicionarTitulo = async (titulo) => {
  const id = Number(titulo.idTituloFinanceiro)
  if (!id) return
  const jaExiste = (item.value.titulosOriginais || []).some(t => Number(t.idTituloFinanceiro) === id)
  if (jaExiste) {
    emit('show-notification', { message: `O título #${id} já está na lista.`, type: 'warning' })
    return
  }

  const valorAlocado = Number(titulo.saldoDevedor) || 0

  // Se o financiamento já foi salvo, usa o endpoint próprio
  if (isFinanciamentoSaved.value && item.value.idOperacaoFinanciamento) {
    try {
      await api.post(`/OperacoesFinanciamentos/${item.value.idOperacaoFinanciamento}/titulos-originais`, {
        idTituloFinanceiro: id,
        valorAlocado
      })
      await recarregarItem()
      emit('show-notification', { message: `Título #${id} vinculado com sucesso.`, type: 'success' })
    } catch (e) {
      const msg = e.response?.data?.message || 'Erro ao vincular título.'
      emit('show-notification', { message: msg, type: 'error' })
    }
    return
  }

  // Criação: adiciona à lista local (será enviado no salvar)
  item.value.titulosOriginais.push({
    idTituloFinanceiro: id,
    valorAlocado,
    numeroDocumento: titulo.numeroDocumento || '',
    nomeFornecedor: titulo.nomeFornecedor || titulo.nomeParceiroNegocio || '',
    saldoDevedor: valorAlocado
  })
}

const onTituloSelecionado = (val) => {
  adicionarTitulo(val)
  item.value.idTituloOriginalSelecao = null
}

const carregarTitulosDoDocumento = async () => {
  const idDocumento = documentoSelecionado.value?.idDocumentoEntrada
  if (!idDocumento) {
    emit('show-notification', { message: 'Selecione um Documento de Entrada primeiro.', type: 'warning' })
    return
  }
  isLoadingTitulosDocumento.value = true
  try {
    const resp = await api.get('/TitulosFinanceiros', { params: { idDocumentoEntrada: idDocumento } })
    const titulos = Array.isArray(resp.data) ? resp.data : []
    if (titulos.length === 0) {
      emit('show-notification', { message: 'Nenhum título financeiro encontrado para este documento.', type: 'info' })
      return
    }
    let adicionados = 0
    for (const t of titulos) {
      const id = Number(t.idTituloFinanceiro)
      if (!id) continue
      const jaExiste = (item.value.titulosOriginais || []).some(x => Number(x.idTituloFinanceiro) === id)
      if (jaExiste) continue

      const valorAlocado = Number(t.saldoDevedor) || 0

      if (isFinanciamentoSaved.value && item.value.idOperacaoFinanciamento) {
        try {
          await api.post(`/OperacoesFinanciamentos/${item.value.idOperacaoFinanciamento}/titulos-originais`, {
            idTituloFinanceiro: id,
            valorAlocado
          })
          adicionados++
        } catch (e) {
          // Ignora duplicados/erros pontuais e segue
        }
      } else {
        item.value.titulosOriginais.push({
          idTituloFinanceiro: id,
          valorAlocado,
          numeroDocumento: t.numeroDocumento || '',
          nomeFornecedor: t.nomeFornecedor || t.nomeParceiroNegocio || '',
          saldoDevedor: valorAlocado
        })
        adicionados++
      }
    }
    if (isFinanciamentoSaved.value && item.value.idOperacaoFinanciamento) {
      await recarregarItem()
    }
    emit('show-notification', {
      message: adicionados > 0 ? `${adicionados} título(s) adicionado(s) ao financiamento.` : 'Todos os títulos do documento já estão na lista.',
      type: adicionados > 0 ? 'success' : 'info'
    })
  } catch (e) {
    console.error('Erro ao buscar títulos do documento:', e)
    emit('show-notification', { message: 'Erro ao carregar títulos do documento.', type: 'error' })
  } finally {
    isLoadingTitulosDocumento.value = false
  }
}

const removerTitulo = async (idTitulo) => {
  const id = Number(idTitulo)
  if (isFinanciamentoSaved.value && item.value.idOperacaoFinanciamento) {
    try {
      await api.delete(`/OperacoesFinanciamentos/${item.value.idOperacaoFinanciamento}/titulos-originais/${id}`)
      await recarregarItem()
      emit('show-notification', { message: `Título #${id} removido.`, type: 'success' })
    } catch (e) {
      const msg = e.response?.data?.message || 'Erro ao remover título.'
      emit('show-notification', { message: msg, type: 'error' })
    }
    return
  }
  item.value.titulosOriginais = (item.value.titulosOriginais || []).filter(t => Number(t.idTituloFinanceiro) !== id)
}

// --- PEDIDOS DE VENDA (Recebíveis — N:N) ---
const adicionarPedidoVenda = async (val) => {
  const idPedido = Number(val.idPedidoVenda)
  if (!idPedido) return
  const jaExiste = (item.value.pedidosVenda || []).some(p => Number(p.idPedidoVenda) === idPedido)
  if (jaExiste) {
    emit('show-notification', { message: `Pedido #${idPedido} já está vinculado.`, type: 'warning' })
    return
  }

  if (isFinanciamentoSaved.value && item.value.idOperacaoFinanciamento) {
    try {
      await api.post(`/OperacoesFinanciamentos/${item.value.idOperacaoFinanciamento}/pedidos-venda`, {
        idPedidoVenda: idPedido
      })
      await recarregarItem()
      emit('show-notification', { message: `Pedido #${idPedido} vinculado com sucesso.`, type: 'success' })
    } catch (e) {
      const msg = e.response?.data?.message || 'Erro ao vincular pedido.'
      emit('show-notification', { message: msg, type: 'error' })
    }
    return
  }

  // Criação: adiciona à lista local (será enviado no salvar)
  item.value.pedidosVenda.push({
    idPedidoVenda: idPedido,
    nomeCliente: val.nomeCliente || '',
    valorTotalNegociado: val.valorTotalNegociado || 0,
    lstMoeda: val.lstMoeda || '',
    lstStatusVinculo: 'Planejado'
  })
}

const removerPedidoVenda = async (pv) => {
  const idPedido = Number(pv.idPedidoVenda)
  if (isFinanciamentoSaved.value && item.value.idOperacaoFinanciamento) {
    try {
      await api.delete(`/OperacoesFinanciamentos/${item.value.idOperacaoFinanciamento}/pedidos-venda/${idPedido}`)
      await recarregarItem()
      emit('show-notification', { message: `Pedido #${idPedido} desvinculado.`, type: 'success' })
    } catch (e) {
      const msg = e.response?.data?.message || 'Erro ao desvincular pedido.'
      emit('show-notification', { message: msg, type: 'error' })
    }
    return
  }
  item.value.pedidosVenda = (item.value.pedidosVenda || []).filter(p => Number(p.idPedidoVenda) !== idPedido)
}

// --- SALVAR ---
const validarAlocacao = () => {
  if (item.value.lstOrigemFinanciamento !== FIN_FINANCIAMENTO_ORIGEM.Divida) return null
  const financiado = Number(item.value.valorFinanciado) || 0
  if (financiado <= 0) return 'Informe o Valor do Principal antes de vincular os títulos.'
  if (totalValorAlocado.value > financiado) {
    return `O valor alocado nos títulos (${formatarValor(totalValorAlocado.value)}) excede o valor financiado (${formatarValor(financiado)}).`
  }
  if (totalValorAlocado.value < financiado) {
    return `Faltam ${formatarValor(financiado - totalValorAlocado.value)} para alocar. O total alocado deve ser igual ao valor financiado (${formatarValor(financiado)}).`
  }
  return null
}

const salvarFinanciamentoLocalmente = async () => {
  if (isReadOnly.value) return
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    emit('show-notification', { message: 'Selecione uma empresa no topo antes de salvar.', type: 'warning' })
    return
  }

  isSavingFinanciamentoLocal.value = true

  const titulosOriginais = (item.value.titulosOriginais || [])
    .map(t => ({
      idTituloFinanceiro: Number(t.idTituloFinanceiro),
      valorAlocado: Number(t.valorAlocado) || 0
    }))
    .filter(t => t.idTituloFinanceiro)

  const payload = {
    ...item.value,
    idInstituicaoFinanceira: item.value.idInstituicaoFinanceira ? Number(item.value.idInstituicaoFinanceira) : null,
    idLimiteCredito: item.value.idLimiteCredito ? Number(item.value.idLimiteCredito) : null,
    idCondicaoPagamento: item.value.idCondicaoPagamento ? Number(item.value.idCondicaoPagamento) : null,
    lstOrigemFinanciamento: item.value.lstOrigemFinanciamento,
    titulosOriginais
  }
  delete payload.lstStatusFinanciamento
  delete payload.idTituloOriginalSelecao
  delete payload.saldoDevedor

  try {
    const response = await api.post('/OperacoesFinanciamentos', payload)
    item.value = response.data
    isFinanciamentoSaved.value = true
    isEditing.value = true
    entityInfo.value = `Editando ID: ${item.value.idOperacaoFinanciamento}`

    // Se veio da Demanda com idPedidoVenda, vincula automaticamente após salvar
    const pedidoPrefill = props.itemParaEditar?.idPedidoVenda
    if (pedidoPrefill && item.value.lstOrigemFinanciamento === FIN_FINANCIAMENTO_ORIGEM.Recebiveis) {
      try {
        await api.post(`/OperacoesFinanciamentos/${item.value.idOperacaoFinanciamento}/pedidos-venda`, {
          idPedidoVenda: Number(pedidoPrefill)
        })
        await recarregarItem()
        emit('show-notification', { message: 'Financiamento salvo e pedido vinculado automaticamente.', type: 'success' })
      } catch (e) {
        emit('show-notification', { message: 'Financiamento salvo, mas não foi possível vincular o pedido automaticamente.', type: 'warning' })
      }
    } else {
      emit('show-notification', { message: 'Financiamento salvo. Agora pode vincular os títulos.', type: 'success' })
    }
    accordionState.dividas = true
  } catch (error) {
    console.error('Erro ao salvar financiamento:', error)
    const errorMsg = error.response?.data?.message || 'Erro ao salvar o financiamento.'
    emit('show-notification', { message: errorMsg, type: 'error' })
  } finally {
    isSavingFinanciamentoLocal.value = false
  }
}

const submitFormFinanciamento = async () => {
  if (isReadOnly.value) return
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    emit('show-notification', { message: 'Selecione uma empresa no topo antes de salvar.', type: 'warning' })
    return
  }

  const erroAlocacao = validarAlocacao()
  if (erroAlocacao) {
    emit('show-notification', { message: erroAlocacao, type: 'error' })
    return
  }

  const titulosOriginais = (item.value.titulosOriginais || [])
    .map(t => ({
      idTituloFinanceiro: Number(t.idTituloFinanceiro),
      valorAlocado: Number(t.valorAlocado) || 0
    }))
    .filter(t => t.idTituloFinanceiro)

  const payload = {
    ...item.value,
    idInstituicaoFinanceira: item.value.idInstituicaoFinanceira ? Number(item.value.idInstituicaoFinanceira) : null,
    idLimiteCredito: item.value.idLimiteCredito ? Number(item.value.idLimiteCredito) : null,
    idCondicaoPagamento: item.value.idCondicaoPagamento ? Number(item.value.idCondicaoPagamento) : null,
    lstOrigemFinanciamento: item.value.lstOrigemFinanciamento,
    titulosOriginais
  }
  delete payload.lstStatusFinanciamento
  delete payload.idTituloOriginalSelecao
  delete payload.saldoDevedor

  await submitForm('OperacoesFinanciamentos', 'idOperacaoFinanciamento', payload)
}

// Ao editar, preenche a grid com os títulos vindos do backend
watch(() => props.itemParaEditar?.idOperacaoFinanciamento, (novoId) => {
  if (novoId) {
    documentoSelecionado.value = null
    isFinanciamentoSaved.value = true
  }
})

// Prefill da Demanda: se veio com idPedidoVenda, marca origem Recebíveis
watch(() => props.itemParaEditar?.idPedidoVenda, (idPedidoVenda) => {
  if (idPedidoVenda && !item.value.idOperacaoFinanciamento) {
    item.value.lstOrigemFinanciamento = FIN_FINANCIAMENTO_ORIGEM.Recebiveis
  }
})

onMounted(() => {
  carregarListas()
})
</script>

<template>
  <FormularioCadastro
    module-name="Operação de Financiamento"
    :is-loading="isLoading || isLoadingListas"
    :is-editing="isEditing"
    :entity-info="customEntityInfo"
    :status="statusExibicao"
    :accordion-state="accordionState"
    :desabilitar-salvar="isReadOnly"
    :ocultar-salvar="!isFinanciamentoSaved"
    label-botao-adicionar="Salvar Financiamento"
    @submit="submitFormFinanciamento"
    @cancelar="handleCancel"
  >
    <template #acoes-header v-if="isEditing">
      <button type="button" class="btn-icon-minimalista me-3" @click="abrirAuditoria" title="Ver Histórico de Alterações">
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <!-- BARRA DE AÇÕES DO FINANCIAMENTO (Toolbar) -->
    <div v-if="isFinanciamentoSaved" class="d-flex justify-content-end align-items-center gap-2 mb-3">
      <button
        v-if="podeCancelar"
        type="button"
        class="btn btn-sm btn-outline-danger fw-bold"
        :disabled="isLoadingAcao"
        @click="confirmarCancelar"
      >
        <i class="bi bi-x-lg me-1"></i>
        Cancelar
      </button>
      <button
        v-if="podeContratar"
        type="button"
        class="btn btn-sm btn-outline-success fw-bold"
        :disabled="isLoadingAcao"
        @click="confirmarContratar"
      >
        <span v-if="isLoadingAcao" class="spinner-border spinner-border-sm me-1"></span>
        <i v-else class="bi bi-check2-circle me-1"></i>
        Contratar
      </button>
    </div>

    <div v-if="isReadOnly" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
      <div><strong>Modo de Leitura:</strong> Esta operação não está em Em Digitação ou o seu perfil não tem permissões para editá-la.</div>
    </div>

    <!-- FRAME 1: DADOS DA ESTRUTURAÇÃO (com observação) -->
    <div class="card mb-4 border-primary-subtle">
      <div
        class="card-header bg-primary-subtle text-primary-emphasis fw-semibold py-2 cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.estruturacao = !accordionState.estruturacao"
      >
        <span><i class="bi bi-bank me-2"></i>Dados da Estruturação</span>
        <i :class="['bi', accordionState.estruturacao ? 'bi-chevron-up' : 'bi-chevron-down', 'text-primary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.estruturacao">
        <div class="row g-3">
          <div class="col-md-3">
            <CampoBuscaLista
              id="lstTipoFinanciamento"
              label="Tipo de Operação"
              v-model="item.lstTipoFinanciamento"
              tabela="OperacaoFinanciamento"
              :required="true"
              :disabled="isReadOnly"
            />
          </div>
          <div class="col-md-3">
            <CampoBusca
              id="idInstituicaoFinanceira"
              label="Instituição Financeira (Banco)"
              v-model="item.idInstituicaoFinanceira"
              :configuracao-busca="configBuscaBanco"
              @selecionado="val => item.nomeInstituicaoFinanceira = val.nomeFantasia"
              :disabled="isReadOnly"
              required
            />
          </div>
          <div class="col-md-3">
            <CampoBusca
              id="idLimiteCredito"
              label="Limite de Crédito"
              v-model="item.idLimiteCredito"
              :configuracao-busca="configBuscaLimiteCredito"
              @selecionado="val => item.descricaoLimiteCredito = val.descricao"
              :disabled="isReadOnly"
            />
          </div>
          <div class="col-md-3">
            <CampoBuscaLista
              id="lstMoeda"
              label="Moeda"
              v-model="item.lstMoeda"
              tabela="OperacaoFinanciamento"
              :required="true"
              :disabled="isReadOnly"
            />
          </div>
          <div class="col-md-3">
            <CampoBuscaLista
              id="lstStatusFinanciamento"
              label="Status do Financiamento"
              v-model="item.lstStatusFinanciamento"
              tabela="OperacaoFinanciamento"
              disabled
            />
          </div>
          <div class="col-md-12">
            <label class="form-label">Observações e Histórico</label>
            <textarea
              id="observacaoLivre"
              class="form-control"
              rows="3"
              v-model="item.observacaoLivre"
              :disabled="isReadOnly"
            ></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- FRAME 2: VALORES E PRAZOS -->
    <div class="card mb-4">
      <div
        class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
        @click="accordionState.valores = !accordionState.valores"
      >
        <h5 class="mb-0"><i class="bi bi-calendar-event me-2 text-primary"></i> Valores e Prazos</h5>
        <i :class="['bi', accordionState.valores ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
      </div>
      <div class="card-body" v-show="accordionState.valores">
        <div class="row g-3">
          <div class="col-md-3">
            <CampoInput id="dataFechamento" label="Data de Fechamento" type="date" v-model="item.dataFechamento" required :disabled="isReadOnly" />
          </div>
          <div class="col-md-3">
            <CampoInput id="dataDesembolso" label="Data de Desembolso" type="date" v-model="item.dataDesembolso" required :disabled="isReadOnly" />
          </div>
          <div class="col-md-3">
            <CampoBusca
              id="idCondicaoPagamento"
              label="Condição de Pagamento"
              v-model="item.idCondicaoPagamento"
              :configuracao-busca="configBuscaCondicaoPagamento"
              :disabled="isReadOnly"
            />
          </div>
          <div class="col-md-3">
            <CampoInput id="taxaJurosAnual" label="Taxa de Juros (Anual) %" type="percentual" v-model="item.taxaJurosAnual" :disabled="isReadOnly" />
          </div>
          <div class="col-md-3">
            <CampoInput id="valorFinanciado" label="Valor do Principal" type="moeda" v-model="item.valorFinanciado" required :disabled="isReadOnly" />
          </div>
          <div class="col-md-3">
            <CampoInput id="valorJuros" label="Valor Total de Juros" type="moeda" v-model="item.valorJuros" required :disabled="isReadOnly" />
          </div>
          <div class="col-md-3">
            <CampoInput id="valorIof" label="Valor IOF" type="moeda" v-model="item.valorIof" :disabled="isReadOnly" />
          </div>
        </div>
      </div>
    </div>

    <!-- Botão Salvar e Continuar (apenas criação) -->
    <div class="mb-4" v-if="!isFinanciamentoSaved">
      <button
        type="button"
        class="btn btn-success px-4"
        @click="salvarFinanciamentoLocalmente"
        :disabled="isSavingFinanciamentoLocal || !item.lstTipoFinanciamento || !item.idInstituicaoFinanceira || !item.lstMoeda || !item.dataFechamento || !item.dataDesembolso"
      >
        <span v-if="isSavingFinanciamentoLocal" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="bi bi-save me-2"></i>
        Salvar Financiamento e Liberar Títulos
      </button>
    </div>

    <!-- FRAME 4: DÍVIDAS A FINANCIAR (GRUPO A) — ÚLTIMO -->
    <div :class="{ 'pe-none opacity-50': isTitulosFrameBlocked }">
      <div class="card mb-4">
        <div
          class="card-header bg-body-tertiary cursor-pointer d-flex justify-content-between align-items-center"
          @click="accordionState.dividas = !accordionState.dividas"
        >
          <h5 class="mb-0"><i class="bi bi-list-check me-2 text-primary"></i> Dívidas a Financiar (Grupo A)</h5>
          <div class="d-flex align-items-center gap-3">
            <span class="badge bg-light text-primary fs-6">{{ (item.titulosOriginais || []).length }} título(s)</span>
            <i :class="['bi', accordionState.dividas ? 'bi-chevron-up' : 'bi-chevron-down', 'text-secondary']"></i>
          </div>
        </div>
        <div class="card-body" v-show="accordionState.dividas">
          <div v-if="!isFinanciamentoSaved" class="text-center text-muted py-4">
            <i class="bi bi-info-circle me-2"></i>
            Salve o financiamento primeiro para vincular os títulos.
          </div>
          <template v-else>
            <!-- Seletor de Origem -->
            <div class="row g-3 mb-3">
              <div class="col-md-4">
                <CampoSelect
                  id="lstOrigemFinanciamento"
                  label="Origem da Operação"
                  v-model="item.lstOrigemFinanciamento"
                  :disabled="isReadOnly"
                  :opcoes-simples="[
                    { value: FIN_FINANCIAMENTO_ORIGEM.Divida, text: 'Dívida (Importação)' },
                    { value: FIN_FINANCIAMENTO_ORIGEM.Recebiveis, text: 'Recebíveis (Exportação)' },
                    { value: FIN_FINANCIAMENTO_ORIGEM.Direto, text: 'Direto (Capital de Giro)' }
                  ]"
                  option-value="value"
                  option-label="text"
                />
              </div>
            </div>

            <!-- Frame: Dívida (Importação) — Documento de Entrada + Títulos A PAGAR -->
            <template v-if="item.lstOrigemFinanciamento === FIN_FINANCIAMENTO_ORIGEM.Divida">
              <div class="row g-3">
                <div class="col-md-6">
                  <CampoBusca
                    id="idDocumentoEntrada"
                    label="Documento de Entrada"
                    v-model="documentoSelecionado"
                    :configuracao-busca="configBuscaDocumentoEntrada"
                    @selecionado="val => documentoSelecionado = val"
                    :disabled="isReadOnly"
                  />
                </div>
                <div class="col-md-2 d-flex align-items-end">
                  <button
                    type="button"
                    class="btn btn-outline-primary w-100"
                    @click="carregarTitulosDoDocumento"
                    :disabled="isReadOnly || isLoadingTitulosDocumento"
                  >
                    <span v-if="isLoadingTitulosDocumento" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="bi bi-download me-1"></i>
                    Carregar Títulos
                  </button>
                </div>
                <div class="col-md-4">
                  <CampoBusca
                    id="idTituloOriginal"
                    label="Título Financeiro (adiciona direto)"
                    v-model="item.idTituloOriginalSelecao"
                    :configuracao-busca="configBuscaTitulo"
                    @selecionado="onTituloSelecionado"
                    :disabled="isReadOnly"
                  />
                </div>
              </div>

              <div v-if="valorAlocadoExcede" class="alert alert-danger border-0 rounded-3 mt-3 d-flex align-items-center">
                <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i>
                <div>O valor alocado nos títulos ({{ formatarValor(totalValorAlocado) }}) excede o valor financiado ({{ formatarValor(item.valorFinanciado) }}).</div>
              </div>
              <div v-else-if="valorAlocadoIncompleto" class="alert alert-warning border-0 rounded-3 mt-3 d-flex align-items-center">
                <i class="bi bi-info-circle-fill fs-4 me-3"></i>
                <div>Faltam {{ formatarValor((Number(item.valorFinanciado) || 0) - totalValorAlocado) }} para alocar. O total alocado deve ser igual ao valor financiado ({{ formatarValor(item.valorFinanciado) }}).</div>
              </div>

              <div class="table-responsive mt-3">
                <table class="table table-sm align-middle">
                  <thead class="table-light">
                    <tr>
                      <th scope="col">#Título</th>
                      <th scope="col">Documento</th>
                      <th scope="col">Fornecedor</th>
                      <th scope="col" class="text-end">Saldo Devedor</th>
                      <th scope="col" class="text-end" style="width: 160px;">Valor Alocado</th>
                      <th scope="col" style="width: 50px;"></th>
                    </tr>
                  </thead>
                  <tbody v-if="(item.titulosOriginais || []).length">
                    <tr v-for="t in item.titulosOriginais" :key="t.idTituloFinanceiro">
                      <td class="fw-medium">#{{ t.idTituloFinanceiro }}</td>
                      <td>{{ t.numeroDocumento || '-' }}</td>
                      <td>{{ t.nomeFornecedor || '-' }}</td>
                      <td class="text-end">{{ formatarValor(t.saldoDevedor) }}</td>
                      <td class="text-end" style="width: 160px;">
                        <div class="input-group input-group-sm">
                          <span class="input-group-text">R$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            class="form-control text-end"
                            v-model.number="t.valorAlocado"
                            :disabled="isReadOnly"
                          />
                        </div>
                      </td>
                      <td class="text-center">
                        <button type="button" class="btn-icon text-danger" @click="removerTitulo(t.idTituloFinanceiro)" :disabled="isReadOnly" title="Remover">
                          <i class="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="6" class="text-center text-muted py-4">
                        Nenhum título adicionado. Selecione um documento e clique em "Carregar Títulos" ou busque um título diretamente.
                      </td>
                    </tr>
                  </tbody>
                  <tfoot v-if="(item.titulosOriginais || []).length">
                    <tr class="table-light fw-bold">
                      <td colspan="4" class="text-end">Total Alocado</td>
                      <td class="text-end">R$ {{ formatarValor(totalValorAlocado) }}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </template>

            <!-- Frame: Recebíveis (Exportação) — PedidosVenda (N:N) -->
            <template v-else-if="item.lstOrigemFinanciamento === FIN_FINANCIAMENTO_ORIGEM.Recebiveis">
              <div class="row g-3">
                <div class="col-md-6">
                  <CampoBusca
                    id="idPedidoVendaVinculo"
                    label="Adicionar Pedido de Venda"
                    v-model="item.idPedidoVendaSelecao"
                    :configuracao-busca="configBuscaPedidoVenda"
                    @selecionado="adicionarPedidoVenda"
                    :disabled="isReadOnly"
                  />
                </div>
              </div>
              <div class="table-responsive mt-3">
                <table class="table table-sm align-middle">
                  <thead class="table-light">
                    <tr>
                      <th scope="col">#Pedido</th>
                      <th scope="col">Cliente</th>
                      <th scope="col" class="text-end">Valor</th>
                      <th scope="col">Moeda</th>
                      <th scope="col">Status</th>
                      <th scope="col" style="width: 50px;"></th>
                    </tr>
                  </thead>
                  <tbody v-if="(item.pedidosVenda || []).length">
                    <tr v-for="pv in item.pedidosVenda" :key="pv.idOperacaoFinanciamentoPedidoVenda">
                      <td class="fw-medium">#{{ pv.idPedidoVenda }}</td>
                      <td>{{ pv.nomeCliente || '-' }}</td>
                      <td class="text-end">{{ formatarValor(pv.valorTotalNegociado) }}</td>
                      <td>{{ pv.lstMoeda || '-' }}</td>
                      <td>{{ pv.lstStatusVinculo || '-' }}</td>
                      <td class="text-center">
                        <button type="button" class="btn-icon text-danger" @click="removerPedidoVenda(pv)" :disabled="isReadOnly" title="Remover">
                          <i class="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="6" class="text-center text-muted py-4">
                        Nenhum pedido de venda vinculado. Adicione um pedido com OperacaoFinanciada = true.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>

            <!-- Frame: Direto — sem origem (oculto) -->
            <div v-else-if="item.lstOrigemFinanciamento === FIN_FINANCIAMENTO_ORIGEM.Direto" class="text-center text-muted py-4">
              <i class="bi bi-info-circle me-2"></i>
              Financiamento direto (Capital de Giro / ACC). Nenhum vínculo de origem necessário.
            </div>
          </template>
        </div>
      </div>
    </div>
  </FormularioCadastro>

  <ModalFormularioV2
    v-model="mostrarAuditoria"
    titulo="Histórico de Alterações"
    tamanho="xl"
    :cor-header="'#34495E'"
  >
    <AuditLogViewer
      embedded
      tabela="OperacaoFinanciamento"
      :chave-primaria="item.idOperacaoFinanciamento"
      :titulo-registro="`Financiamento ${item.nomeInstituicaoFinanceira || item.idOperacaoFinanciamento}`"
      contexto="tenant"
      :tabelas-filhas="['OperacaoFinanciamentoTituloOriginal']"
      chave-estrangeira="IdOperacaoFinanciamento"
      :dicionarios="{
        LstTipoFinanciamento: listas.LstTipoFinanciamento,
        LstStatusFinanciamento: listas.LstStatusFinanciamento,
        LstMoeda: listas.LstMoeda
      }"
      @fechar="mostrarAuditoria = false"
    />
  </ModalFormularioV2>

  <ConfirmationModal
    ref="confirmModalRef"
    titulo="Confirmar Ação"
    :mensagem="mensagemConfirmacao"
    botao-confirmar="Confirmar"
    botao-cancelar="Cancelar"
    tipo="warning"
    @confirm="onConfirmAcao"
  />
</template>
